import { useState, useEffect } from "react";
import { AlertTriangle, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import PrintReleaseFormButton from "./PrintReleaseFormButton";
import PrintReceipt from "./PrintReceipt";
import { salvageShort, salvageFull } from "../lib/company";

/**
 * Sign over a non-impound tow job (AA, SN, manual) to NTS in lieu of
 * unpaid tow + storage fees. Mirrors the impound NTS flow but the
 * dispatcher enters the bad-debt amount manually because tow jobs don't
 * carry a pricing engine the way impounds do.
 */
export default function SignToNtsDialog({ job, open, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Oct 2026 — this screen used to print the release form only, while the
  // impound sign-over screen also printed the write-off record. Same action,
  // two different paper trails depending on which button you reached it from.
  const [receipt, setReceipt] = useState(null);

  // Pre-fill amount with whatever invoice-amount field exists on the job (if any).
  useEffect(() => {
    if (open) {
      setAmount(job?.invoice_amount ? String(job.invoice_amount) : "");
      setNotes("");
      setReceipt(null);
    }
  }, [open, job]);

  // Stay mounted on the receipt snapshot even after the parent clears the
  // job — otherwise the print-only write-off record unmounts before
  // window.print() fires and the browser prints the page underneath.
  if (!job && !receipt) return null;

  const submit = async () => {
    const v = Number(amount);
    if (!Number.isFinite(v) || v < 0) {
      toast.error("Amount must be a positive number");
      return;
    }
    setSaving(true);
    try {
      const { data: updated } = await api.post(`/jobs/${job.id}/release-to-nts`, {
        amount_owed: v,
        release_notes: notes,
      });
      setReceipt({
        ...job,
        ...(updated || {}),
        release_notes: notes,
        // Tow jobs carry no pricing engine, so the write-off record has
        // nothing to total up — feed it the amount the dispatcher typed.
        pricing_breakdown: updated?.pricing_breakdown || { total: v },
      });
      toast.success(`Vehicle signed over to ${salvageShort()} — printing write-off record`);
      setTimeout(() => window.print(), 250);
      // `onDone` clears the job in the parent, which would unmount us mid-print
      // — it runs when the dispatcher closes the done dialog instead.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign over");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    {job && (
    <Dialog open={open && !receipt} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sign-to-nts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Sign Over to {salvageShort()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-700 dark:text-slate-200 rounded-md bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
            <div className="font-semibold">{job.customer_name || "—"}</div>
            <div className="text-xs text-slate-500">
              {job.vehicle || "—"}{job.rego ? ` · ${job.rego}` : ""}
            </div>
          </div>
          <div className="text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded p-2 text-amber-900 dark:text-amber-100">
            Vehicle ownership transfers to {salvageFull()} in lieu of payment. The amount below is recorded as bad debt on the {salvageShort()} Owned in Lieu reconciliation dashboard.
          </div>
          <div>
            <Label className="uppercase-label">Amount Owed (ex-GST)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="sign-nts-amount"
              autoFocus
            />
            <p className="text-[11px] text-slate-500 mt-1">Total of tow + storage + admin fees that won&apos;t be paid.</p>
          </div>
          <div>
            <Label className="uppercase-label">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Customer uncontactable, vehicle abandoned 3 weeks"
              data-testid="sign-nts-notes"
            />
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <PrintReleaseFormButton
            jobId={job?.id}
            action="signover_to_nts"
            label="Print Release Form"
            testId="sign-nts-print-form"
            className="mr-auto"
          />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || amount === ""}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="sign-nts-submit"
          >
            {saving ? <Loader2 className="h-4 w-4.5 animate-spin" /> : <AlertTriangle className="h-4 w-4.5" />}
            Confirm Sign-Over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    )}

    {receipt && (
      <Dialog open onOpenChange={(o) => { if (!o) { setReceipt(null); onDone?.(); } }}>
        <DialogContent className="max-w-md" data-testid="sign-nts-done-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 dark:text-amber-300">Signed over to {salvageShort()}</DialogTitle>
            <DialogDescription>If the print dialog didn&apos;t appear, click below to reopen it.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.print()} data-testid="sign-nts-reprint"><Printer className="h-3.5 w-3.5 mr-1.5"/>Re-print</Button>
            <Button onClick={() => { setReceipt(null); onDone?.(); }} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="sign-nts-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {receipt && <PrintReceipt job={receipt} mode="nts" />}
    </>
  );
}
